/**
 * useDeviceCommand Hook — Send commands to IoT devices via device_commands table
 *
 * Inserts command rows into Supabase, subscribes to Realtime for status
 * transitions (pending → sent → confirmed | timeout | rejected).
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/supabase-client'

const CLEAR_DELAY_MS = 3000

export function useDeviceCommand(deviceType, deviceId) {
  const [pendingCommand, setPendingCommand] = useState(null)
  const clearTimerRef = useRef(null)

  useEffect(() => {
    const channel = supabase
      .channel(`cmd-${deviceType}-${deviceId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'device_commands',
        filter: `device_type=eq.${deviceType}`
      }, (payload) => {
        const row = payload.new
        if (row.device_id !== deviceId) return

        setPendingCommand(prev => {
          if (!prev || prev.id !== row.id) return prev
          if (['confirmed', 'timeout', 'rejected'].includes(row.status)) {
            clearTimerRef.current = setTimeout(() => setPendingCommand(null), CLEAR_DELAY_MS)
          }
          return { ...prev, status: row.status }
        })
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
    }
  }, [deviceType, deviceId])

  const sendCommand = useCallback(async (commandKey, value) => {
    if (pendingCommand?.status === 'pending' || pendingCommand?.status === 'sent') {
      return { error: 'Command already in flight' }
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { error: 'Not authenticated' }

    const { data, error } = await supabase
      .from('device_commands')
      .insert([{
        device_type: deviceType,
        device_id: deviceId,
        command_key: commandKey,
        value,
        created_by: user.id
      }])
      .select()
      .single()

    if (error) {
      console.error('[useDeviceCommand] Insert error:', error)
      return { error: error.message }
    }

    if (clearTimerRef.current) clearTimeout(clearTimerRef.current)
    setPendingCommand({ id: data.id, command_key: commandKey, value, status: 'pending' })
    return { data }
  }, [deviceType, deviceId, pendingCommand])

  return {
    sendCommand,
    pendingCommand,
    isCommandPending: pendingCommand?.status === 'pending' || pendingCommand?.status === 'sent',
  }
}

export default useDeviceCommand
